/**
 * O servidor de blocos está respondendo? Enquanto sim, a base do satélite fica
 * em `LARGURA_BASE` e o detalhe vem em blocos; se ele cair, tudo volta a ser
 * como antes (arquivo inteiro do STAR na largura que a tela pede).
 *
 * - Na partida, um pedido a `/saude` (tempo-limite curto). Enquanto ele não
 *   volta, vale "verificando": a base já fica limitada, para não começar
 *   baixando 7200 px que os blocos tornariam inúteis.
 * - Durante o uso, falhas de REDE seguidas (ou 5xx) derrubam; 404 não conta —
 *   é o horário que o STAR ainda não publicou, e o arquivo inteiro também não
 *   existiria —, nem 503: o servidor está no ar e pede para tentar de novo
 *   (JPEG do STAR ainda incompleto; o carregador respeita o `Retry-After`).
 * - `/saude` anuncia os níveis de cada produto além de 3600/7200 (`niveis`,
 *   ex.: `{"02": [3600, 7200, 14400]}`) e as larguras inteiras que espelha do
 *   STAR (`inteiros`, ex.: `[450, 900, 1800]`; ver espelho.ts).
 * - A verificação que falha tenta de novo uma vez, 1 s depois (uma conexão nova
 *   presa por 13–15 s acontece em ~2,5% das vezes, e a próxima passa).
 * - Fora do ar: nova verificação em 30 s nas primeiras vezes, depois a cada 5 min.
 */
import { create } from 'zustand';
import { isImageWidth } from '../goes/star';
import { fetchWithRetry, HttpError, isAbortError, sleep } from '../lib/fetchRetry';
import { BLOCOS_URL, ehNivel, NIVEIS_PADRAO, type Nivel } from './blocos';

export type EstadoBlocos = 'verificando' | 'ok' | 'fora';

interface SaudeBlocos {
  estado: EstadoBlocos;
  /** Níveis anunciados por produto (só os que diferem do padrão). */
  niveis: Record<string, readonly Nivel[]>;
  /** Larguras inteiras do STAR espelhadas pelo servidor (espelho.ts). */
  inteiros: readonly number[];
}

export const useBlocosStore = create<SaudeBlocos>()(() => ({
  estado: BLOCOS_URL ? 'verificando' : 'fora',
  niveis: {},
  inteiros: [],
}));

/** Níveis de blocos do produto (anunciados pelo servidor, ou 3600/7200). */
export function niveisDo(produto: string, niveis = useBlocosStore.getState().niveis): readonly Nivel[] {
  return niveis[produto] ?? NIVEIS_PADRAO;
}

/** O campo `niveis` do `/saude`, só com níveis conhecidos (3600 e 7200 sempre). Puro — testado. */
export function lerNiveis(bruto: unknown): Record<string, readonly Nivel[]> {
  const out: Record<string, readonly Nivel[]> = {};
  if (!bruto || typeof bruto !== 'object') return out;
  for (const [produto, lista] of Object.entries(bruto as Record<string, unknown>)) {
    if (!Array.isArray(lista)) continue;
    const niveis = [...new Set([...NIVEIS_PADRAO, ...lista.filter(ehNivel)])].sort((a, b) => a - b);
    if (niveis.length > NIVEIS_PADRAO.length) out[produto] = niveis;
  }
  return out;
}

/** O campo `inteiros` do `/saude`: larguras do STAR (450…7200), sem repetição. Puro — testado. */
export function lerInteiros(bruto: unknown): number[] {
  if (!Array.isArray(bruto)) return [];
  return [...new Set(bruto.filter((w): w is number => typeof w === 'number' && isImageWidth(w)))].sort((a, b) => a - b);
}

/** Os blocos podem ser usados (ou provavelmente vão poder, na partida)? */
export const blocosDisponiveis = (estado: EstadoBlocos = useBlocosStore.getState().estado) => estado !== 'fora';

const TEMPO_VERIFICACAO_MS = 4000;
/** Segunda tentativa da verificação que falhou. */
const SEGUNDA_TENTATIVA_MS = 1000;
/** Fora do ar: as primeiras novas verificações vêm logo; depois, espaçadas. */
const NOVA_VERIFICACAO_CEDO_MS = 30_000;
const VERIFICACOES_CEDO = 3;
const NOVA_VERIFICACAO_MS = 5 * 60_000;
/** Falhas de rede seguidas que tiram os blocos do ar. */
export const FALHAS_PARA_DESISTIR = 6;

let falhasSeguidas = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
/** Novas verificações já agendadas com o servidor fora (zera quando volta). */
let verificacoesFora = 0;

/** Espera até a próxima verificação com o servidor fora. Puro — testado. */
export const esperaNovaVerificacao = (feitas: number) => (feitas < VERIFICACOES_CEDO ? NOVA_VERIFICACAO_CEDO_MS : NOVA_VERIFICACAO_MS);

function definir(estado: EstadoBlocos): void {
  if (useBlocosStore.getState().estado !== estado) useBlocosStore.setState({ estado });
  if (timer) clearTimeout(timer);
  timer = null;
  if (estado !== 'fora') verificacoesFora = 0;
  else if (BLOCOS_URL) timer = setTimeout(() => void verificarBlocos(), esperaNovaVerificacao(verificacoesFora++));
}

interface RespostaSaude {
  ok?: boolean;
  niveis?: unknown;
  inteiros?: unknown;
}

const pedirSaude = () =>
  fetchWithRetry(
    `${BLOCOS_URL}/saude`,
    { mode: 'cors', credentials: 'omit', cache: 'no-store' },
    { retries: 0, timeoutMs: TEMPO_VERIFICACAO_MS, read: (r) => r.json() as Promise<RespostaSaude> },
  );

/** Pergunta ao servidor se ele está no ar (uma segunda vez, 1 s depois, se falhar). */
export async function verificarBlocos(): Promise<EstadoBlocos> {
  if (!BLOCOS_URL) return 'fora';
  let saude: RespostaSaude | null = null;
  for (let tentativa = 0; tentativa < 2 && !saude; tentativa++) {
    if (tentativa > 0) await sleep(SEGUNDA_TENTATIVA_MS);
    saude = await pedirSaude().catch(() => null);
  }
  if (!saude) {
    definir('fora');
    return 'fora';
  }
  falhasSeguidas = 0;
  const niveis = lerNiveis(saude.niveis);
  const inteiros = lerInteiros(saude.inteiros);
  const atual = useBlocosStore.getState();
  if (JSON.stringify(niveis) !== JSON.stringify(atual.niveis)) useBlocosStore.setState({ niveis });
  if (JSON.stringify(inteiros) !== JSON.stringify(atual.inteiros)) useBlocosStore.setState({ inteiros });
  definir(saude.ok ? 'ok' : 'fora');
  return useBlocosStore.getState().estado;
}

/** Conta o resultado de um download de bloco (ver o carregador em cache.ts). */
export function registrarBloco(erro: unknown | null): void {
  if (erro === null) {
    falhasSeguidas = 0;
    return;
  }
  if (isAbortError(erro)) return;
  // 4xx: o bloco não existe (ainda); 503: o servidor pede para tentar depois.
  if (erro instanceof HttpError && (erro.status < 500 || erro.status === 503)) return;
  falhasSeguidas++;
  if (falhasSeguidas >= FALHAS_PARA_DESISTIR && useBlocosStore.getState().estado !== 'fora') {
    console.warn('[AmaView] Servidor de blocos sem resposta; o satélite volta ao arquivo inteiro.', erro);
    definir('fora');
  }
}

/** (testes) volta ao começo. */
export function reiniciarSaude(estado: EstadoBlocos = BLOCOS_URL ? 'verificando' : 'fora'): void {
  falhasSeguidas = 0;
  if (timer) clearTimeout(timer);
  timer = null;
  verificacoesFora = 0;
  useBlocosStore.setState({ estado, niveis: {}, inteiros: [] });
}
